/**
 * V70 Phase 3-C — 신규 BE Play Callable raw wrapper
 *
 * S7 QA 배포 완료된 8개 Callable 을 httpsCallable 로 직접 호출.
 * 응답을 FE interface 로 변환하는 로직은 marble/server/serverApi.ts 와
 * acidRain/server/serverApi.ts 가 담당 — 본 파일은 **얇은 bridge 만**.
 *
 * fail-closed: 호출 실패 시 Error throw (상위에서 catch).
 */
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../../firebase/utils';

// =================================================================
// 공통 타입 — BE types.ts 와 동기화
// =================================================================
export type GameKey = 'acidRain' | 'marble';

export interface InProgressEventStateServer {
  eventId: string;
  kind: string;  // EventKind — 'E1'~'E10', 'safe', 'vault', 'shop' 등
  createdAt: number;
  options?: Array<{
    id: string | number;
    label: string;
    rewards?: Array<{ value: number; prob: number; label?: string }>;
  }>;
}

export interface MarbleSnapshotPlayerState {
  uid: string;
  gameKey: 'marble';
  epoch: number;
  position: number;
  totalScore: number;
  items: { shields: number; speedBoosts: number };
  isTrapped: boolean;
  trapFails: number;
  lapsCompleted: number;
  lapsReward: number;
  playCount: number;
  highScoreEvents: number;
  lossSum: number;
  lifetimeRollSeq: number;
  inProgressEvent: InProgressEventStateServer | null;
  currentShopVisit: { rollSeq: number; purchased: string[] } | null;
  version: number;
}

// =================================================================
// 1. marbleRollDice
// =================================================================
export interface MarbleRollInput {
  clientRequestId: string;
  useSpeedBoost?: boolean;
}
export interface MarbleRollResult {
  rollId: string;
  dice: number;
  from: number;
  to: number;
  cellKind: string;
  immediate?: {
    delta: number;
    scoreAfter: number;
    shieldConsumed?: boolean;
    grossLoss?: number;
    label?: string;
    /** QA Issue #4: E8 (방패 획득 칸) cap 초과 시 BE가 명시. FE가 "최대 보유 · 획득 실패" 안내 분기. */
    shieldsCapReached?: boolean;
  };
  pending?: InProgressEventStateServer;
  lapsReward?: number;
}
export interface MarbleRollResp {
  success: true;
  reused: boolean;
  sessionId: string;
  rollResult: MarbleRollResult;
  playerStateSnapshot: MarbleSnapshotPlayerState;
  wasReset: boolean;
}
export async function callMarbleRollDice(input: MarbleRollInput): Promise<MarbleRollResp> {
  const fn = httpsCallable<MarbleRollInput, MarbleRollResp>(getFunctions(), 'marbleRollDice');
  const r = await fn(input);
  return r.data;
}

// =================================================================
// 2. marbleSubmitEventChoice
// =================================================================
export interface MarbleSubmitEventInput {
  clientRequestId: string;
  eventId: string;
  choice: string | number | null;
}
export interface MarbleSubmitEventResp {
  success: true;
  reused: boolean;
  reqId: string;
  eventId: string;
  delta: number;
  scoreAfter: number;
  shieldConsumed: boolean;
  grossLoss?: number;
  outcomeLabel?: string;
  drawnNumber?: number;
  /** QA Issue #3: E9 워프 destination cell kind (E8/E10/safe). FE 체인 애니메이션 분기용. */
  arrivalCellKind?: 'E8' | 'E10' | 'safe';
  /** QA Issue #4: E9→E8 체인 방패 cap 초과 시 BE가 명시. */
  shieldsCapReached?: boolean;
  // at: Timestamp — ms 변환은 wrapper 가 처리 (현재는 참고용 raw 수신)
  at?: { _seconds?: number; _nanoseconds?: number } | string | number;
  playerStateSnapshot: MarbleSnapshotPlayerState;
}
export async function callMarbleSubmitEventChoice(input: MarbleSubmitEventInput): Promise<MarbleSubmitEventResp> {
  const fn = httpsCallable<MarbleSubmitEventInput, MarbleSubmitEventResp>(getFunctions(), 'marbleSubmitEventChoice');
  const r = await fn(input);
  return r.data;
}

// =================================================================
// 3. marblePurchaseItem — BE 는 'pass' 도 지원 ('그냥 지나가기')
// =================================================================
export type MarblePurchaseItemKey = 'shield' | 'speedBoost' | 'pass';
export interface MarblePurchaseInput {
  clientRequestId: string;
  item: MarblePurchaseItemKey;
}
export interface MarblePurchaseResp {
  success: true;
  reused: boolean;
  reqId: string;
  item: MarblePurchaseItemKey;
  priceDeducted: number;
  shieldsAfter: number;
  speedBoostsAfter: number;
  scoreAfter: number;
  at?: unknown;  // Timestamp
  arrivedAtRollSeq?: number;
  playerStateSnapshot: MarbleSnapshotPlayerState;
}
export async function callMarblePurchaseItem(input: MarblePurchaseInput): Promise<MarblePurchaseResp> {
  const fn = httpsCallable<MarblePurchaseInput, MarblePurchaseResp>(getFunctions(), 'marblePurchaseItem');
  const r = await fn(input);
  return r.data;
}

// =================================================================
// 4. getActiveMarbleSession
// =================================================================
export interface GetActiveMarbleSessionResp {
  playerState: MarbleSnapshotPlayerState | null;
  pendingEvent?: InProgressEventStateServer | null;
  isResumable: boolean;
  currentEpoch?: number;
  playerEpoch?: number;
  seasonExpired?: boolean;
}
export async function callGetActiveMarbleSession(): Promise<GetActiveMarbleSessionResp> {
  const fn = httpsCallable<Record<string, never>, GetActiveMarbleSessionResp>(getFunctions(), 'getActiveMarbleSession');
  const r = await fn({});
  return r.data;
}

// =================================================================
// 5. consumeAcidRainSession
// =================================================================
export interface ConsumeAcidRainInput {
  clientRequestId: string;
}
export interface AcidRainSessionSettingsSnapshot {
  initialLives: number;
  durationSec: number;
  difficultyRampSec: number;
  fallSpeedMultiplier: number;
  spawnIntervalDivisor: number;
  spawnAlgorithmVersion: string;
  wordDictionaryVersion: number;
}
export interface ConsumeAcidRainResp {
  success: true;
  reused: boolean;
  sessionId: string;
  seed: string;
  startedAtServerMs: number;
  durationSec: number;
  settings: AcidRainSessionSettingsSnapshot;
  leaderboardEpoch: number;
}
export async function callConsumeAcidRainSession(input: ConsumeAcidRainInput): Promise<ConsumeAcidRainResp> {
  const fn = httpsCallable<ConsumeAcidRainInput, ConsumeAcidRainResp>(getFunctions(), 'consumeAcidRainSession');
  const r = await fn(input);
  return r.data;
}

// =================================================================
// 6. appendAcidRainInputEvents
// =================================================================
export interface AcidRainServerInputEvent {
  seq: number;
  word: string;
  type: 'cleared' | 'missed';
  elapsedMs: number;
  combo: number;
}
export interface AppendAcidRainInput {
  sessionId: string;
  clientRequestId: string;
  events: AcidRainServerInputEvent[];
}
export interface AppendAcidRainResp {
  success: true;
  committed: number;
  nextExpectedSeq: number;
  /**
   * Phase 3-C.2 — chunk 멱등 응답.
   * true: 이미 저장된 chunk 와 완전 동일 재전송 (committed=0, nextExpectedSeq 불변).
   * false/undefined: 신규 처리.
   */
  reused?: boolean;
}
export async function callAppendAcidRainInputEvents(input: AppendAcidRainInput): Promise<AppendAcidRainResp> {
  const fn = httpsCallable<AppendAcidRainInput, AppendAcidRainResp>(getFunctions(), 'appendAcidRainInputEvents');
  const r = await fn(input);
  return r.data;
}

// =================================================================
// 7. submitAcidRainScore
// =================================================================
export interface SubmitAcidRainInput {
  sessionId: string;
  clientRequestId: string;
  result: {
    clientReportedScore: number;
    cleared: number;
    missed: number;
    maxCombo: number;
    reason: 'time_up' | 'no_lives' | 'user_quit';
  };
}
export interface SubmitAcidRainResp {
  success: true;
  reused: boolean;
  serverScore: number;
  clientReportedScore?: number;
  scoreDiff: number;
  flagged: 'none' | 'low' | 'high' | 'macro' | 'invalid';
  rank: number | null;
  isPersonalBest: boolean;
  bestScore: number;
}
export async function callSubmitAcidRainScore(input: SubmitAcidRainInput): Promise<SubmitAcidRainResp> {
  const fn = httpsCallable<SubmitAcidRainInput, SubmitAcidRainResp>(getFunctions(), 'submitAcidRainScore');
  const r = await fn(input);
  return r.data;
}

// =================================================================
// 8. getPublicTournamentLeaderboard
// =================================================================
export interface PublicLeaderboardInput {
  gameKey: GameKey;
  limit?: number;
}
export interface PublicLeaderboardRow {
  rank: number;
  uid: string;
  bestScore: number;
  playCount: number;
  displayName: string;
  orgUnitId: string | null;
  companyCode: 'KR' | 'CN' | null;
  marble?: { highScoreEvents: number; lossSum: number };
}
export interface PublicLeaderboardResp {
  success: true;
  rows: PublicLeaderboardRow[];
  self: PublicLeaderboardRow | null;
  epoch: number;
}
export async function callGetPublicTournamentLeaderboard(input: PublicLeaderboardInput): Promise<PublicLeaderboardResp> {
  const fn = httpsCallable<PublicLeaderboardInput, PublicLeaderboardResp>(getFunctions(), 'getPublicTournamentLeaderboard');
  const r = await fn(input);
  return r.data;
}

// =================================================================
// 9. getTournamentDailyStatus (Phase 3-C.1 결함 D 수정)
// =================================================================
export interface DailyStatusInput { gameKey: GameKey }
export interface DailyStatusResp {
  success: true;
  gameKey: GameKey;
  mode: 'limited' | 'unlimited';
  used: number;
  limit: number | null;
  remaining: number | null;
  ymd: string;
  // V70G8D — 마블 주말(KST 토·일): 신규 지급 없이 금요일 잔여분 사용 중
  weekendCarryover?: boolean;
}
export async function callGetTournamentDailyStatus(input: DailyStatusInput): Promise<DailyStatusResp> {
  const fn = httpsCallable<DailyStatusInput, DailyStatusResp>(getFunctions(), 'getTournamentDailyStatus');
  const r = await fn(input);
  return r.data;
}

// =================================================================
// 10. QA Test Panel — ngle-worksqa 전용 (BE 런타임 project+role guard)
// =================================================================
export interface QaMarbleResetInput { clientRequestId: string }
export interface QaMarbleResetResp {
  success: true;
  reused: boolean;
  ymd: string;
  epoch: number;
}
export async function callQaMarbleResetMyState(input: QaMarbleResetInput): Promise<QaMarbleResetResp> {
  const fn = httpsCallable<QaMarbleResetInput, QaMarbleResetResp>(getFunctions(), 'qaMarbleResetMyState');
  const r = await fn(input);
  return r.data;
}

export interface QaMarbleRefillInput { clientRequestId: string }
export interface QaMarbleRefillResp {
  success: true;
  reused: boolean;
  grantedAmount: number;
  ymd: string;
  qaBonusRemaining: number;
}
export async function callQaMarbleRefillDice(input: QaMarbleRefillInput): Promise<QaMarbleRefillResp> {
  const fn = httpsCallable<QaMarbleRefillInput, QaMarbleRefillResp>(getFunctions(), 'qaMarbleRefillDice');
  const r = await fn(input);
  return r.data;
}
